import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  buyerCombinedIntroApi,
  type DraftBuyerCombinedIntroPayload,
  type SendBuyerCombinedIntroPayload,
  type UpdateBuyerCombinedIntroPayload,
} from "@/api/buyers";

import { buyerKeys, buyerSubmissionKeys } from "./buyerKeys";

/**
 * The combined intro email's reads and writes — one email to one buyer
 * introducing several of their listings.
 *
 * <p>🔴 <b>A send or a copy moves more than the draft.</b> It stamps each
 * listing's intro-sent date, appends a version to each listing's send history,
 * and moves the buyer's contact clock — so both prefixes are invalidated, the
 * buyer's (the draft, the record) AND the submissions' (the table's "Intro
 * sent" caption, every listing's history). Invalidating only the draft would
 * leave the table saying nothing was sent for five minutes.</p>
 */

/** The buyer's open draft, or null — what the tab reads to offer "Resume
 *  draft". */
export function useOpenCombinedIntroDraft(buyerId: string, enabled = true) {
  return useQuery({
    queryKey: buyerKeys.combinedIntroEmail(buyerId),
    queryFn: () => buyerCombinedIntroApi.getOpenDraft(buyerId),
    enabled,
  });
}

export function useDraftCombinedIntro(buyerId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: DraftBuyerCombinedIntroPayload) =>
      buyerCombinedIntroApi.draft(buyerId, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: buyerKeys.combinedIntroEmail(buyerId) });
    },
  });
}

export function useUpdateCombinedIntro(buyerId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdateBuyerCombinedIntroPayload }) =>
      buyerCombinedIntroApi.update(buyerId, id, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: buyerKeys.combinedIntroEmail(buyerId) });
    },
  });
}

export function useSendCombinedIntro(buyerId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: SendBuyerCombinedIntroPayload }) =>
      buyerCombinedIntroApi.send(buyerId, id, body),
    // Settled, not success: an email that went but could not be recorded is a
    // 200 with `recorded: false`, and a refusal is an error — both leave the
    // server in a state the screen should re-read.
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: buyerKeys.all });
      void qc.invalidateQueries({ queryKey: buyerSubmissionKeys.all });
    },
  });
}

export function useCopyCombinedIntro(buyerId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: SendBuyerCombinedIntroPayload }) =>
      buyerCombinedIntroApi.copy(buyerId, id, body),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: buyerKeys.all });
      void qc.invalidateQueries({ queryKey: buyerSubmissionKeys.all });
    },
  });
}
